import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, FileText, DollarSign, CheckCircle, ArrowRight } from "lucide-react";
import { getCurrentInsurance, type InsuranceCompanyUser } from "@/Calls/callInsuranceAuth";
import { Card } from "@/components/ui/card";

export const InsuranceDashboard = () => {
    const [company, setCompany] = useState<InsuranceCompanyUser | null>(null);

    useEffect(() => {
        getCurrentInsurance().then(setCompany);
    }, []);

    const statCards = [
        { label: "Available Leads",    icon: Shield,       color: "text-blue-500",   to: "/insurance/quote-requests" },
        { label: "My Submitted Quotes", icon: FileText,    color: "text-violet-500", to: "/insurance/my-quotes"      },
        { label: "Accepted Policies",   icon: CheckCircle, color: "text-green-500",  to: "/insurance/my-quotes"      },
        { label: "Billing Balance",     icon: DollarSign,  color: "text-amber-500",  to: "/insurance/billing"        },
    ];

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">
                    {company ? company.company_name : "Insurance Portal"}
                </h1>
                <p className="text-muted-foreground text-sm mt-1">
                    {company?.company_type ?? "Insurance Company"} · {company?.status ?? ""}
                    {company?.vehicle_types_covered ? ` · ${(company.vehicle_types_covered as string[]).join(", ")}` : " · All Vehicle Types"}
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map(({ label, icon: Icon, color, to }) => (
                    <Link key={label} to={to}>
                        <Card className="p-5 hover:shadow-md transition-shadow cursor-pointer">
                            <Icon className={`h-8 w-8 ${color} mb-3`} />
                            <p className="text-sm text-muted-foreground">{label}</p>
                        </Card>
                    </Link>
                ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
                <Card className="p-6">
                    <h3 className="font-semibold mb-4">Quick Actions</h3>
                    <div className="space-y-3">
                        <Link to="/insurance/quote-requests" className="flex items-center justify-between p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors">
                            <span className="text-sm font-medium">Browse Available Leads</span>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </Link>
                        <Link to="/insurance/my-quotes" className="flex items-center justify-between p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors">
                            <span className="text-sm font-medium">View Submitted Quotes</span>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </Link>
                        <Link to="/insurance/billing" className="flex items-center justify-between p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors">
                            <span className="text-sm font-medium">View Billing Statement</span>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </Link>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="font-semibold mb-4">Account Info</h3>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Company Code</span>
                            <span className="font-mono">{company?.company_code ?? "—"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Lead Rate</span>
                            <span className="font-semibold text-green-600">${company?.lead_rate ?? "35.00"} / lead</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Type</span>
                            <span>{company?.is_major ? "Major Carrier" : company?.company_type ?? "—"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Status</span>
                            <span className={`font-medium ${company?.status === "ACTIVE" ? "text-green-600" : "text-amber-600"}`}>
                                {company?.status ?? "—"}
                            </span>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default InsuranceDashboard;
